import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { toast } from '@/design-system/toaster';
import {
  DeliveryDetail,
  DeliveryStatus,
  deliveriesAPI,
} from '@/services/api/deliveries';
import { DeliveryStatusBadge, SlaPill } from '../components/DeliveryStatusBadge';
import ConfirmActionModal from './ConfirmActionModal';
import CancelDeliveryModal from './CancelDeliveryModal';
import AssignDriverModal from './AssignDriverModal';
import RetryDeliveryModal, {
  RetryReasonCode,
} from '@/components/RetryDeliveryModal';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import AuthContext from '@/contexts/AuthContext';
import { canRetryDelivery } from '@/config/ops-permissions';

// Firebase Remote Config flag (JSON) — same key the back-office uses so a
// single allowlist governs both apps. Shape: { providerIDs: number[] }.
// Only providers in this list get the Failed/In-Return action buttons
// (Mark as In Return / Mark as Returned / Mark as Disposed). Rename here
// if logistic gets its own dedicated Remote Config key.
const FAILED_HANDLE_FLAG_KEY = 'back_office_failed_handle_providerIDs';

/** Safely read the providerIDs allowlist out of the remote-config JSON. */
const readFailedHandleProviderIDs = (
  flag: Record<string, unknown> | null
): Array<number | string> => {
  if (!flag) return [];
  const ids = (flag as { providerIDs?: unknown }).providerIDs;
  return Array.isArray(ids) ? (ids as Array<number | string>) : [];
};

// ── Format helpers ─────────────────────────────────────────────────────
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return (
    d.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) +
    ' ' +
    d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
  );
};

const fmtCurrency = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(n);
};

/**
 * Format a schedule pickup window the same way as backoffice's
 * `formatPickupTimeReview`: "Senin, 09:00 - 17:00"
 * Shows day name + time range (no redundant date on both ends).
 */
const fmtScheduleWindow = (
  from: string | null | undefined,
  to: string | null | undefined
): string => {
  if (!from || !to) return '—';
  const fromDate = new Date(from);
  const toDate = new Date(to);
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime()))
    return '—';
  const day = fromDate.toLocaleDateString('id-ID', { weekday: 'long' });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  return `${day}, ${fmtTime(fromDate)} - ${fmtTime(toDate)}`;
};

/** Trim a lat/lng float to 6 decimals (≈11cm precision — plenty). */
const fmtCoord = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return n.toFixed(6);
};

/** Humanize SNAKE_CASE / camelCase strings into "Snake Case" / "Camel Case". */
const humanize = (s: string | null | undefined): string => {
  if (!s) return '—';
  return s
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const metersToKm = (m: number | null | undefined): string => {
  if (m == null) return '—';
  return `${(m / 1000).toFixed(2)} km`;
};

// Backoffice's `formatWeekdayDate` — "Rabu, 27 Mei 2026"
const fmtWeekdayDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
};

// Backoffice's `formatHourMinute` — "12.53" (id-ID locale uses . separator)
const fmtHourMinute = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
};

// Mirrors backoffice `identityType` enum
const IDENTITY_LABEL: Record<string, string> = {
  WEB: 'Back Office',
  CUSTOMER: 'Customer',
  DRIVER: 'Mitra',
  'INTERNAL-SERVICE': 'System',
  'PROVIDER-H2H': 'H2H',
  'PROVIDER-USER': 'Next Portal',
  'CLIENT-USER': 'Next Portal',
  SYSTEM: 'System',
};

// ── Section card wrapper ───────────────────────────────────────────────
const SectionCard: React.FC<{
  icon: string;
  title: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, headerRight, children }) => (
  <div className="rounded-lg border border-stroke-soft-200 bg-bg-white-0">
    <div className="flex items-center justify-between border-b border-stroke-soft-200 px-5 py-3.5">
      <div className="flex items-center gap-2">
        <i className={`${icon} text-[20px] text-text-sub-600`} />
        <span className="text-sm font-bold tracking-tight text-text-strong-950">
          {title}
        </span>
      </div>
      {headerRight && <div>{headerRight}</div>}
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

// ── Two-column detail row ──────────────────────────────────────────────
const DetailRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
      {label}
    </p>
    <div className="text-[13px] font-medium text-text-strong-950 leading-snug">
      {children}
    </div>
  </div>
);

// ── Avatar group with lightbox (matches backoffice VerticalProgressBar) ──
const TimelineAvatarGroup: React.FC<{ media: Array<{ url: string }> }> = ({
  media,
}) => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const shown = media.slice(0, 3);
  const extra = media.length - shown.length;
  return (
    <>
      <div
        className="flex items-center -space-x-2 cursor-pointer mt-1"
        onClick={() => setOpenIdx(0)}
      >
        {shown.map((m, i) => (
          <img
            key={i}
            src={m.url}
            alt={`Proof ${i + 1}`}
            className="h-12 w-12 rounded-full border-2 border-white object-cover shadow-sm transition-transform hover:scale-105"
          />
        ))}
        {extra > 0 && (
          <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-bg-soft-200 text-[12px] font-bold text-text-sub-600 shadow-sm">
            +{extra}
          </div>
        )}
      </div>
      {openIdx !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setOpenIdx(null)}
        >
          <img
            src={media[openIdx].url}
            alt={`Proof ${openIdx + 1}`}
            className="max-h-screen max-w-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {media.length > 1 && (
            <>
              {openIdx > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenIdx((i) => (i ?? 0) - 1);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
                >
                  <i className="ri-arrow-left-s-line text-2xl" />
                </button>
              )}
              {openIdx < media.length - 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenIdx((i) => (i ?? 0) + 1);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
                >
                  <i className="ri-arrow-right-s-line text-2xl" />
                </button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

// ── Vertical timeline (mirrors backoffice VerticalProgressBar) ─────────
const Timeline: React.FC<{
  entries: DeliveryDetail['timeline'];
}> = ({ entries }) => {
  if (!entries || entries.length === 0) {
    return (
      <p className="text-sm text-text-soft-400 py-4 text-center">
        Belum ada riwayat pengiriman
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {entries.map((step, index) => {
        const isFirst = index === 0;
        const isLast = index === entries.length - 1;
        const driverName = step.driver?.name;
        const prevDriverName = step.previousDriver?.name;
        const identity = step.createdBy?.source
          ? IDENTITY_LABEL[step.createdBy.source] ?? step.createdBy.source
          : null;
        return (
          <div key={index} className="flex flex-row gap-4">
            {/* Left: date/time column */}
            <div className="flex flex-col gap-1 items-end min-w-[140px] pt-0.5">
              <p
                className={`text-[13px] font-medium text-right ${
                  isFirst ? 'text-text-strong-950' : 'text-text-sub-600'
                }`}
              >
                {fmtWeekdayDate(step.createdAt)}
              </p>
              <p className="text-[13px] text-text-sub-600 text-right tabular-nums">
                {fmtHourMinute(step.createdAt)} WIB
              </p>
            </div>

            {/* Center: rail */}
            <div className="flex flex-col items-center">
              <i
                className={`ri-checkbox-circle-fill text-[20px] ${
                  isFirst ? 'text-accent' : 'text-text-soft-400'
                }`}
              />
              {!isLast && (
                <div className="h-full w-px border-l border-dashed border-stroke-soft-200 my-0.5" />
              )}
            </div>

            {/* Right: content */}
            <div className="flex flex-col w-full ml-1 mb-4 gap-1">
              <div className="flex">
                <DeliveryStatusBadge status={step.status} />
              </div>

              {step.media && step.media.length > 0 && (
                <TimelineAvatarGroup
                  media={step.media as Array<{ url: string }>}
                />
              )}

              {driverName && (
                <p className="text-[12px] text-text-sub-600">
                  Mitra:{' '}
                  {prevDriverName && (
                    <span>{prevDriverName} to </span>
                  )}
                  {driverName}
                </p>
              )}

              {step.notes && (
                <p className="text-[12px] text-text-sub-600">
                  Notes: {step.notes}
                </p>
              )}

              {step.createdBy?.name && (
                <p className="text-[12px] text-text-sub-600">
                  Updated By {step.createdBy.name}
                  {identity && (
                    <span className="text-text-sub-600"> ({identity})</span>
                  )}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── COD status pill ────────────────────────────────────────────────────
const CodStatusPill: React.FC<{ isPaid: boolean }> = ({ isPaid }) => (
  <Badge status={isPaid ? 'success' : 'error'} type="dot">
    {isPaid ? 'Paid' : 'Unpaid'}
  </Badge>
);

// ── Main page ──────────────────────────────────────────────────────────
const DeliveryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Firebase Remote Config allowlist: which providers may run the
  // failed-handling flow. While the flag loads it is `null` → banner stays
  // hidden until config resolves (matches back-office behaviour).
  const failedHandleConfig = useFeatureFlag(FAILED_HANDLE_FLAG_KEY, 'json');

  // useContext, not useAuth: fail closed (deny) outside a provider.
  const user = useContext(AuthContext)?.user;

  const [data, setData] = useState<DeliveryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDisposedModal, setShowDisposedModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!id) return;
      if (opts.silent) setRefreshing(true);
      else setLoading(true);
      try {
        const detail = await deliveriesAPI.getDetail(id);
        setData(detail);
      } catch (err) {
        toast.error('Gagal memuat detail delivery', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      } finally {
        if (opts.silent) setRefreshing(false);
        else setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (status: DeliveryStatus) => {
    if (!data) return;
    setActionLoading(true);
    let succeeded = false;
    try {
      const res = await deliveriesAPI.updateStatus(data.deliveryID, status);
      toast.success('Status diperbarui', { description: res?.message ?? '' });
      setShowDisposedModal(false);
      succeeded = true;
    } catch (err) {
      toast.error('Gagal memperbarui status', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionLoading(false);
    }
    if (succeeded) await fetchData({ silent: true });
  };

  const handleCancel = async (notes: string, reasonCode: string) => {
    if (!data) return;
    setActionLoading(true);
    let succeeded = false;
    try {
      const res = await deliveriesAPI.cancel(data.deliveryID, {
        notes,
        reasonCode,
      });
      toast.success('Delivery dibatalkan', {
        description: res?.message ?? 'Delivery cancelled',
      });
      setShowCancelModal(false);
      succeeded = true;
    } catch (err) {
      toast.error('Gagal membatalkan delivery', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionLoading(false);
    }
    if (succeeded) await fetchData({ silent: true });
  };

  const handleAssignDriver = async (
    driverId: string,
    notes: string,
    reasonCode: string | null
  ) => {
    if (!data) return;
    setActionLoading(true);
    let succeeded = false;
    try {
      const res = await deliveriesAPI.assignDriver(data.deliveryID, driverId, {
        notes,
        reasonCode,
      });
      toast.success('Driver berhasil ditugaskan', {
        description: res?.message ?? 'Driver assigned',
      });
      setShowAssignModal(false);
      succeeded = true;
    } catch (err) {
      toast.error('Gagal menugaskan driver', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionLoading(false);
    }
    if (succeeded) await fetchData({ silent: true });
  };

  const handleRetry = async (reasonCode: RetryReasonCode, notes: string) => {
    if (!data) return;
    setActionLoading(true);
    let succeeded = false;
    try {
      const res = await deliveriesAPI.retry(data.deliveryID, {
        reasonCode,
        notes,
      });
      toast.success('Delivery dikirim ulang', {
        description:
          res?.message ??
          'Status kembali ke Allocating — shipment & outbound request ter-reset dalam request yang sama.',
      });
      setShowRetryModal(false);
      succeeded = true;
    } catch (err) {
      toast.error('Gagal mengirim ulang delivery', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActionLoading(false);
    }
    if (succeeded) await fetchData({ silent: true });
  };

  const isFailed = data?.status === 'FAILED';
  const isInReturn = data?.status === 'IN_RETURN';

  // Gate the failed-handling banner on the Firebase Remote Config
  // allowlist, mirroring back-office: the delivery's provider must be
  // listed in `back_office_failed_handle_providerIDs.providerIDs`.
  // The comparison tolerates string-vs-number id mismatches.
  const isProviderFailedHandled = (() => {
    if (data?.providerID == null) return false;
    const allowlist = readFailedHandleProviderIDs(failedHandleConfig);
    return allowlist.some(
      (item) =>
        item === data.providerID ||
        Number(item) === Number(data.providerID) ||
        String(item) === String(data.providerID)
    );
  })();

  // The retry has its own gate: a build-time email allowlist
  // (REACT_APP_DELIVERY_RETRY_EMAILS, fail closed — see ops-permissions.ts).
  // UI affordance only; delivery-service re-checks eligibility on the write.
  const showRetryAction = isFailed && canRetryDelivery(user?.email);
  const showFailedHandleActions =
    isProviderFailedHandled && (isFailed || isInReturn);
  const showActionBanner = showFailedHandleActions || showRetryAction;

  return (
    <div className="space-y-5 p-6">
      {/* ── Page header ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 border-b border-stroke-soft-200 pb-5">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-stroke-soft-200 bg-bg-white-0 hover:bg-bg-weak-50 transition-colors"
            aria-label="Back"
          >
            <i className="ri-arrow-left-s-line text-[18px] text-text-sub-600" />
          </button>
          <div className="min-w-0">
            <p className="label-eyebrow mb-1 flex items-center gap-1.5 text-text-sub-600">
              <button
                type="button"
                onClick={() => navigate('/delivery')}
                className="hover:text-accent transition-colors"
              >
                Delivery
              </button>
              <i className="ri-arrow-right-s-line text-[12px] text-text-soft-400" />
              <span className="font-mono text-text-sub-600 truncate">
                {data?.providerOrderID || data?.deliveryID || id}
              </span>
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-[22px] font-extrabold tracking-tight text-text-strong-950 leading-none">
                Detail Delivery
              </h1>
              {data && <DeliveryStatusBadge status={data.status} />}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => fetchData({ silent: true })}
            disabled={refreshing || loading}
          >
            <i
              className={`mr-1.5 text-[13px] ${
                refreshing ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'
              }`}
            />
            {refreshing ? 'Memuat…' : 'Refresh'}
          </Button>
          {data?.isCancellable && (
            <Button
              tone="destructive"
              style="stroke"
              size="sm"
              onClick={() => setShowCancelModal(true)}
            >
              <i className="ri-close-circle-line mr-1.5 text-[13px]" />
              Cancel Delivery
            </Button>
          )}
        </div>
      </div>

      {/* ── Loading ─────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 text-text-soft-400">
          <i className="ri-loader-4-line animate-spin text-3xl mb-3 text-accent" />
          <p className="text-sm">Sedang memuat data…</p>
        </div>
      ) : !data ? (
        <div className="flex flex-col items-center justify-center py-24">
          <i className="ri-inbox-line text-5xl text-text-soft-400 mb-4" />
          <p className="text-sm font-semibold text-text-sub-600">
            Data detail Delivery tidak tersedia
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* ── §8.3 Failed / In-Return action banner ──────────── */}
          {showActionBanner && (
            <div className="rounded-lg border border-(--state-information-light) bg-(--state-information-lighter)">
              <div className="flex items-center gap-2 border-b border-(--state-information-light) px-4 py-3 bg-(--state-information-light)/60 rounded-t-gsm">
                <i className="ri-information-line text-(--state-information-base)" />
                <p className="text-sm font-semibold text-(--state-information-dark)">
                  {isFailed
                    ? 'Paket Gagal Dikirim'
                    : 'Paket Sedang Dikembalikan'}
                </p>
              </div>
              <div className="px-4 py-3 space-y-3">
                <p className="text-sm text-(--state-information-dark) leading-relaxed">
                  {isFailed
                    ? 'Pengiriman gagal diselesaikan karena alasan tertentu. Jika diperlukan, pilih status akhir di bawah ini untuk menandai tindakan lanjutan seperti return, reject, atau dispose.'
                    : 'Paket saat ini dalam proses pengembalian oleh kurir ke lokasi asal. Setelah paket diterima kembali, ubah statusnya untuk menandai bahwa return telah selesai.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {showFailedHandleActions && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() =>
                        handleStatusChange(isFailed ? 'IN_RETURN' : 'RETURNED')
                      }
                      className="inline-flex items-center gap-1.5 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-3 py-1.5 text-sm font-medium hover:bg-bg-weak-50 disabled:opacity-50 transition-colors"
                    >
                      <i
                        className={`text-[14px] ${isFailed ? 'ri-loop-left-line' : 'ri-archive-2-line'}`}
                      />
                      {isFailed ? 'Mark as In Return' : 'Mark as Returned'}
                    </button>
                  )}
                  {showFailedHandleActions && isFailed && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => setShowDisposedModal(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-3 py-1.5 text-sm font-medium hover:bg-bg-weak-50 disabled:opacity-50 transition-colors"
                    >
                      <i className="ri-delete-bin-line text-[14px]" />
                      Mark as Disposed
                    </button>
                  )}
                  {showRetryAction && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => setShowRetryModal(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-accent bg-(--theme-accent-alpha-10) px-3 py-1.5 text-sm font-semibold text-accent hover:bg-(--theme-accent-alpha-16) disabled:opacity-50 transition-colors"
                    >
                      <i className="ri-restart-line text-[14px]" />
                      Retry Delivery (Kirim Ulang)
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── §8.4 Data Delivery ──────────────────────────────── */}
          <SectionCard icon="ri-truck-line" title="Data Delivery">
            <div className="grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-2">
              {/* Left column */}
              <div className="space-y-4">
                <DetailRow label="Status Delivery">
                  <DeliveryStatusBadge status={data.status} />
                </DetailRow>
                <DetailRow label="Client Name">
                  {data.client?.name ?? '—'}
                </DetailRow>
                <DetailRow label="Provider Order ID">
                  <span className="font-mono">{data.providerOrderID ?? '—'}</span>
                </DetailRow>
                <DetailRow label="Delivery ID">
                  <span className="font-mono">{data.deliveryID}</span>
                </DetailRow>
                <DetailRow label="Tipe Layanan">
                  {data.quote?.service?.name ?? '—'}
                </DetailRow>
                <DetailRow label="Business Unit">
                  {humanize(data.revenueStream)}
                </DetailRow>
                <DetailRow label="One-Time Delivery Code">
                  <span className="font-mono">
                    {data.policies?.oneTimeCode ?? '—'}
                  </span>
                </DetailRow>
                <DetailRow label="Outlet Name">
                  {data.clientOutlet?.name ?? '—'}
                </DetailRow>
                <DetailRow label="Keterangan">
                  {data.status === 'CANCELLED' && data.cancelledReason
                    ? data.cancelledReason
                    : data.status === 'FAILED' && data.failedReason
                      ? data.failedReason
                      : '—'}
                </DetailRow>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <DetailRow label="Waktu Maksimal Pengambilan">
                  {fmtDate(data.max_pickup_at)}
                </DetailRow>
                <DetailRow label="Waktu Pengambilan">
                  {data.pickupAt
                    ? fmtDate(data.pickupAt)
                    : data.schedule
                      ? fmtScheduleWindow(
                          data.schedule.pickupTimeFrom,
                          data.schedule.pickupTimeTo
                        )
                      : '—'}
                </DetailRow>
                <DetailRow label="SLA Status Pengambilan">
                  <SlaPill status={data.pickup_sla_status} />
                </DetailRow>
                <DetailRow label="Waktu Maksimal Penyelesaian">
                  {fmtDate(data.max_completion_at)}
                </DetailRow>
                <DetailRow label="Waktu Penyelesaian">
                  {fmtDate(data.completedAt)}
                </DetailRow>
                <DetailRow label="SLA Status Penyelesaian">
                  <SlaPill status={data.complete_sla_status} />
                </DetailRow>
                <DetailRow label="Jarak Antaran (km)">
                  {metersToKm(data.quote?.distance)}
                </DetailRow>
                <DetailRow label="Informasi Tambahan">
                  {data.additionalInfo &&
                  Object.keys(data.additionalInfo).length > 0 ? (
                    <div className="space-y-0.5">
                      {Object.entries(data.additionalInfo).map(([k, v]) => (
                        <p key={k}>
                          <span className="font-semibold">
                            {k === 'patient' ? 'Nama Pasien' : humanize(k)}:
                          </span>{' '}
                          {v || '—'}
                        </p>
                      ))}
                    </div>
                  ) : (
                    '—'
                  )}
                </DetailRow>
              </div>
            </div>
          </SectionCard>

          {/* ── §8.5 Data COD ───────────────────────────────────── */}
          {data.cashOnDelivery && (
            <SectionCard icon="ri-money-dollar-circle-line" title="Data COD">
              <div className="grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-2">
                <div className="space-y-4">
                  <DetailRow label="Metode Pembayaran">
                    {humanize(data.cashOnDelivery.codPaymentMethod)}
                  </DetailRow>
                  <DetailRow label="Dikonfirmasi oleh">
                    {data.cashOnDelivery.codConfirmedBy?.name ?? '—'}
                  </DetailRow>
                </div>
                <div className="space-y-4">
                  <DetailRow label="Nominal (COD)">
                    <span className="tabular-nums">
                      {fmtCurrency(data.cashOnDelivery.amount)}
                    </span>
                  </DetailRow>
                  <DetailRow label="Status COD">
                    {data.cashOnDelivery.codPaymentMethod === 'CASH' ? (
                      <CodStatusPill isPaid={data.cashOnDelivery.isPaid} />
                    ) : (
                      '—'
                    )}
                  </DetailRow>
                </div>
              </div>
            </SectionCard>
          )}

          {/* ── §8.6 Driver ─────────────────────────────────────── */}
          <SectionCard
            icon="ri-user-line"
            title="Driver"
            headerRight={
              <div className="flex items-center gap-2">
                {/* Copy phone number */}
                {data.courier?.phone && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard
                        .writeText(data.courier!.phone)
                        .then(() =>
                          toast.success('Nomor disalin', {
                            description: data.courier!.phone,
                          })
                        );
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-2.5 py-1 text-[12px] font-semibold text-text-sub-600 hover:bg-bg-weak-50 transition-colors"
                  >
                    <i className="ri-file-copy-line text-[13px]" />
                    Copy Number
                  </button>
                )}
                {/* Assign / Change driver */}
                {data.isReassignable && (
                  <button
                    type="button"
                    onClick={() => setShowAssignModal(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-(--theme-accent-alpha-10) px-2.5 py-1 text-[12px] font-semibold text-accent hover:bg-(--theme-accent-alpha-16) transition-colors"
                  >
                    <i className="ri-edit-line text-[13px]" />
                    {data.courier ? 'Change Driver' : 'Assign Driver'}
                  </button>
                )}
              </div>
            }
          >
            <div className="grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-2">
              <DetailRow label="Nama Driver">
                {data.courier?.name ?? '—'}
              </DetailRow>
              <DetailRow label="Phone Number">
                <span className="font-mono">{data.courier?.phone ?? '—'}</span>
              </DetailRow>
            </div>
          </SectionCard>

          {/* ── §8.7 Sender + Recipient ─────────────────────────── */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SectionCard icon="ri-user-4-line" title="Sender Detail">
              <div className="space-y-4">
                <DetailRow label="Nama">
                  {data.sender
                    ? [data.sender.firstName, data.sender.lastName]
                        .filter(Boolean)
                        .join(' ')
                    : '—'}
                </DetailRow>
                <DetailRow label="Phone Number">
                  <span className="font-mono">
                    {data.sender?.phone ?? '—'}
                  </span>
                </DetailRow>
              </div>
            </SectionCard>
            <SectionCard icon="ri-user-received-2-line" title="Recipient Detail">
              <div className="space-y-4">
                <DetailRow label="Nama">
                  {data.recipient
                    ? [data.recipient.firstName, data.recipient.lastName]
                        .filter(Boolean)
                        .join(' ')
                    : '—'}
                </DetailRow>
                <DetailRow label="Phone Number">
                  <span className="font-mono">
                    {data.recipient?.phone ?? '—'}
                  </span>
                </DetailRow>
              </div>
            </SectionCard>
          </div>

          {/* ── §8.8 Lokasi Penjemputan + Pengiriman ────────────── */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Pickup */}
            <SectionCard
              icon="ri-store-2-line"
              title="Lokasi Penjemputan"
              headerRight={
                data.quote?.origin?.coordinates && (
                  <a
                    href={`https://maps.google.com/?q=${data.quote.origin.coordinates.latitude},${data.quote.origin.coordinates.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-2.5 py-1 text-[12px] font-semibold text-text-sub-600 hover:bg-bg-weak-50 transition-colors"
                  >
                    <i className="ri-map-pin-2-line text-[13px]" />
                    Google Maps
                  </a>
                )
              }
            >
              <div className="space-y-4">
                <DetailRow label="Alamat">
                  {data.quote?.origin?.address ?? '—'}
                </DetailRow>
                {/* Only rendered when there is one: most deliveries carry no
                    note, and an always-present empty row would be noise. */}
                {data.quote?.origin?.notes && (
                  <DetailRow label="Catatan untuk rider">
                    <span className="font-normal text-text-sub-600">
                      {data.quote.origin.notes}
                    </span>
                  </DetailRow>
                )}
                <DetailRow label="Latitude">
                  <span className="font-mono tabular-nums">
                    {fmtCoord(data.quote?.origin?.coordinates?.latitude)}
                  </span>
                </DetailRow>
                <DetailRow label="Longitude">
                  <span className="font-mono tabular-nums">
                    {fmtCoord(data.quote?.origin?.coordinates?.longitude)}
                  </span>
                </DetailRow>
              </div>
            </SectionCard>

            {/* Delivery */}
            <SectionCard
              icon="ri-home-pin-2-line"
              title="Lokasi Pengiriman"
              headerRight={
                data.quote?.destination?.coordinates && (
                  <a
                    href={`https://maps.google.com/?q=${data.quote.destination.coordinates.latitude},${data.quote.destination.coordinates.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-2.5 py-1 text-[12px] font-semibold text-text-sub-600 hover:bg-bg-weak-50 transition-colors"
                  >
                    <i className="ri-map-pin-2-line text-[13px]" />
                    Google Maps
                  </a>
                )
              }
            >
              <div className="space-y-4">
                <DetailRow label="Alamat">
                  {data.quote?.destination?.address ?? '—'}
                </DetailRow>
                {data.quote?.destination?.notes && (
                  <DetailRow label="Catatan untuk rider">
                    <span className="font-normal text-text-sub-600">
                      {data.quote.destination.notes}
                    </span>
                  </DetailRow>
                )}
                <DetailRow label="Latitude">
                  <span className="font-mono tabular-nums">
                    {fmtCoord(data.quote?.destination?.coordinates?.latitude)}
                  </span>
                </DetailRow>
                <DetailRow label="Longitude">
                  <span className="font-mono tabular-nums">
                    {fmtCoord(data.quote?.destination?.coordinates?.longitude)}
                  </span>
                </DetailRow>
              </div>
            </SectionCard>
          </div>

          {/* ── §8.9 Data Barang ────────────────────────────────── */}
          <SectionCard icon="ri-box-2-line" title="Data Barang">
            {!data.quote?.packages || data.quote.packages.length === 0 ? (
              <p className="text-sm text-text-soft-400">Tidak ada data barang</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-stroke-soft-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stroke-soft-200 bg-bg-weak-50">
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
                        Nama Barang
                      </th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
                        Berat Barang (Kg)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stroke-soft-200">
                    {data.quote.packages.map((pkg, i) => (
                      <tr key={i} className="hover:bg-bg-weak-50">
                        <td className="px-4 py-2.5 text-[13px] text-text-strong-950">
                          {pkg.name}
                        </td>
                        <td className="px-4 py-2.5 text-[13px] tabular-nums text-text-sub-600">
                          {pkg.weight}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          {/* ── §8.11 Shipment Tracking ─────────────────────────── */}
          <SectionCard
            icon="ri-truck-line"
            title="Shipment Tracking"
            headerRight={
              data.trackingURL ? (
                <a
                  href={data.trackingURL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-stroke-strong-950 bg-bg-white-0 px-3 py-1.5 text-[13px] font-semibold text-text-strong-950 hover:bg-(--theme-accent-alpha-10) hover:border-accent hover:text-accent transition-colors"
                >
                  <i className="ri-map-2-line text-[15px]" />
                  Lacak Pengiriman
                </a>
              ) : undefined
            }
          >
            <Timeline entries={data.timeline ?? []} />
          </SectionCard>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────── */}
      <ConfirmActionModal
        isOpen={showDisposedModal}
        onClose={() => setShowDisposedModal(false)}
        onConfirm={() => handleStatusChange('DISPOSED')}
        isLoading={actionLoading}
        icon="ri-delete-bin-line"
        iconTone="warning"
        title="Tandai paket sebagai disposed?"
        description="Paket yang kamu pilih akan ditandai sebagai disposed dan tidak bisa diedit lagi."
        actionLabel="Ya, Tandai"
      />

      <CancelDeliveryModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        isLoading={actionLoading}
        deliveryStatus={data?.status}
      />

      <AssignDriverModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        deliveryData={data}
        onAssign={handleAssignDriver}
        isLoading={actionLoading}
      />

      <RetryDeliveryModal
        isOpen={showRetryModal}
        onClose={() => setShowRetryModal(false)}
        onConfirm={handleRetry}
        isLoading={actionLoading}
      />
    </div>
  );
};

export default DeliveryDetailPage;
